import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminDetailTableHeader,
  AdminTableBody,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  BLOCK_OPTION_OPTIONS,
  DUPLICATE_CHECK_OPTIONS,
  FLAG_DETAIL_REQUIREMENT_OPTIONS,
  FLAG_TYPE_OPTIONS,
  FLAGGING_SUBMITTED_BY_OPTIONS,
  SUBMISSION_RESULT_OPTIONS,
  buildSampleFlaggingRules,
  findDuplicateFlaggingRule,
  getAvailabilityOptionsForFlagType,
  getBlockOfferingReasonOptions,
  getDetailRequiredReasonOptions,
  getFlagReasonSetOptions,
  needsBlockOfferingReasons,
  needsDetailRequiredReasons,
  nextFlaggingRuleId,
  normalizeFlaggingForm,
  supportsBlockOption,
} from '../../../data/flaggingConfig';
import {
  appendTestHistory,
  collectActivationErrors,
  getEditRuleSource,
  getPublishedRule,
  mergeRulesAfterActivate,
  runFlaggingRuleTest,
} from '../../../data/flaggingRuleWorkflow';
import { getConfigurationRuleTestPath } from '../../../data/configurationTestSimulationConfig';
import ConfigurationSaveTestModal from './shared/ConfigurationSaveTestModal';
import ConfigurationRuleTestResultPanel from './shared/ConfigurationRuleTestResultPanel';
import { matchesUrlName, urlNameSegment } from '../../../utils/slugify';

const emptyForm = () => ({
  id: '',
  ruleName: '',
  flagType: '',
  submittedBy: [],
  flaggingAvailability: '',
  flagReasonSet: '',
  flagDetailRequirement: '',
  detailRequiredReasons: [],
  duplicateCheck: '',
  blockOption: '',
  blockOfferingReasons: [],
  submissionResult: '',
  createdOn: '',
  createdBy: '',
  modifiedOn: '',
  modifiedBy: '',
  version: 1,
  pendingDraft: null,
  configurationHistory: [],
  testHistory: [],
  _editingActiveRevision: false,
});

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeFlaggingForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    submittedBy: normalized.submittedBy ?? [],
    detailRequiredReasons: normalized.detailRequiredReasons ?? [],
    blockOfferingReasons: normalized.blockOfferingReasons ?? [],
    version: normalized.version ?? 1,
    pendingDraft: normalized.pendingDraft ?? null,
    configurationHistory: normalized.configurationHistory ?? [],
    testHistory: normalized.testHistory ?? [],
    _editingActiveRevision: normalized._editingActiveRevision ?? false,
  };
}

const formatAuditDisplay = (value) => {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
};

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const DOWNSTREAM_FROM_TYPE = [
  'submittedBy', 'flaggingAvailability', 'flagReasonSet', 'flagDetailRequirement',
  'detailRequiredReasons', 'duplicateCheck', 'blockOption', 'blockOfferingReasons', 'submissionResult',
];
const DOWNSTREAM_FROM_AVAILABILITY = DOWNSTREAM_FROM_TYPE.slice(2);
const DOWNSTREAM_FROM_REASON_SET = DOWNSTREAM_FROM_TYPE.slice(3);
const DOWNSTREAM_FROM_DETAIL_REQ = ['detailRequiredReasons', 'duplicateCheck', 'blockOption', 'blockOfferingReasons', 'submissionResult'];
const DOWNSTREAM_FROM_BLOCK = ['blockOfferingReasons', 'submissionResult'];

function clearFields(target, fields) {
  fields.forEach((field) => {
    if (field === 'submittedBy' || field === 'detailRequiredReasons' || field === 'blockOfferingReasons') {
      target[field] = [];
    } else {
      target[field] = '';
    }
  });
}

const FlaggingRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleFlaggingRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((r) => r.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const base = allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(base) || base, base.id);
    }
    if (ruleFromState) {
      const base = allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(base, { preferDraft: true }) : getPublishedRule(base) || base;
      return mergeIncomingRule(source, base.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found, { preferDraft: true }) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});
  const [saveTestModal, setSaveTestModal] = useState(null);
  const [testResultFeedback, setTestResultFeedback] = useState(null);
  const [testFeedback, setTestFeedback] = useState('');
  const [testDetail, setTestDetail] = useState('');

  const availabilityOptions = useMemo(() => getAvailabilityOptionsForFlagType(formData.flagType), [formData.flagType]);
  const reasonSetOptions = useMemo(() => getFlagReasonSetOptions(formData.flagType), [formData.flagType]);
  const detailReasonOptions = useMemo(() => getDetailRequiredReasonOptions(formData.flagReasonSet), [formData.flagReasonSet]);
  const blockReasonOptions = useMemo(() => getBlockOfferingReasonOptions(formData.flagReasonSet), [formData.flagReasonSet]);
  const showDetailRequiredReasons = needsDetailRequiredReasons(formData.flagDetailRequirement);
  const showBlockOfferingReasons = needsBlockOfferingReasons(formData.blockOption);
  const blockOptionChoices = useMemo(() => {
    if (!supportsBlockOption(formData.flagType)) {
      return BLOCK_OPTION_OPTIONS.filter((o) => o.value === 'not_available');
    }
    return BLOCK_OPTION_OPTIONS;
  }, [formData.flagType]);

  useEffect(() => { setFormData(resolvedSource); }, [resolvedSource]);

  useEffect(() => {
    const activationError = location.state?.activationError;
    if (!activationError) return;
    setErrors((prev) => ({ ...prev, activation: activationError }));
    const { activationError: _removed, ...rest } = location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const testResult = location.state?.testResult;
    if (!testResult) return;
    setTestResultFeedback(testResult);
    const { testResult: _removed, ...rest } = location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (showDetailRequiredReasons) return;
    setFormData((prev) => (prev.detailRequiredReasons?.length ? { ...prev, detailRequiredReasons: [] } : prev));
  }, [showDetailRequiredReasons, formData.flagDetailRequirement]);

  useEffect(() => {
    if (showBlockOfferingReasons) return;
    setFormData((prev) => (prev.blockOfferingReasons?.length ? { ...prev, blockOfferingReasons: [] } : prev));
  }, [showBlockOfferingReasons, formData.blockOption]);

  useEffect(() => {
    if (!formData.duplicateCheck) return;
    if (!supportsBlockOption(formData.flagType) && formData.blockOption !== 'not_available') {
      setFormData((prev) => ({ ...prev, blockOption: 'not_available', blockOfferingReasons: [] }));
    }
  }, [formData.duplicateCheck, formData.flagType, formData.blockOption]);

  const handleChange = (field, value) => {
    setErrors((prev) => {
      const nextErr = { ...prev, [field]: undefined, activation: undefined };
      if (field === 'flagType') delete nextErr.duplicate;
      return nextErr;
    });
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'flagType') {
        clearFields(next, DOWNSTREAM_FROM_TYPE);
      }
      if (field === 'flaggingAvailability') clearFields(next, DOWNSTREAM_FROM_AVAILABILITY);
      if (field === 'flagReasonSet') clearFields(next, DOWNSTREAM_FROM_REASON_SET);
      if (field === 'flagDetailRequirement') clearFields(next, DOWNSTREAM_FROM_DETAIL_REQ);
      if (field === 'blockOption') clearFields(next, DOWNSTREAM_FROM_BLOCK);
      if (field === 'flagType' && !supportsBlockOption(value)) {
        next.blockOption = 'not_available';
        next.blockOfferingReasons = [];
      }
      return next;
    });
  };

  const toggleArrayValue = (field, value) => {
    if (isView) return;
    setErrors((prev) => ({ ...prev, [field]: undefined, activation: undefined }));
    setFormData((prev) => {
      const list = Array.isArray(prev[field]) ? [...prev[field]] : [];
      const idx = list.indexOf(value);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(value);
      return { ...prev, [field]: list };
    });
  };

  const collectDuplicateError = () => {
    if (!formData.flagType || !formData.submittedBy?.length) return null;
    const dup = findDuplicateFlaggingRule(allRules, formData, formData.id || undefined);
    return dup ? 'An Active duplicate rule already exists for this configuration.' : null;
  };

  const validateFull = () => {
    const next = {};
    if (!formData.ruleName?.trim()) next.ruleName = 'Enter a rule name.';
    if (!formData.flagType) next.flagType = 'Select a flag type.';
    if (!formData.submittedBy?.length) next.submittedBy = 'Select at least one option.';
    if (!formData.flaggingAvailability) next.flaggingAvailability = 'Select flagging availability.';
    if (!formData.flagReasonSet) next.flagReasonSet = 'Select a flag reason set.';
    if (!formData.flagDetailRequirement) next.flagDetailRequirement = 'Select a flag detail requirement.';
    if (showDetailRequiredReasons && !formData.detailRequiredReasons?.length) {
      next.detailRequiredReasons = 'Select at least one detail-required reason.';
    }
    if (!formData.duplicateCheck) next.duplicateCheck = 'Select a duplicate check option.';
    if (!formData.blockOption) next.blockOption = 'Select a block option.';
    if (showBlockOfferingReasons && !formData.blockOfferingReasons?.length) {
      next.blockOfferingReasons = 'Select at least one block-offering reason.';
    }
    if (!formData.submissionResult) next.submissionResult = 'Select a submission result.';
    const dupMsg = collectDuplicateError();
    if (dupMsg) next.duplicate = dupMsg;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildRecord = (nowIso, userLabel) => {
    const id = formData.id || nextFlaggingRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...normalizeFlaggingForm(formData),
      id,
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: formData.pendingDraft ?? null,
      configurationHistory: formData.configurationHistory || [],
      testHistory: formData.testHistory || [],
      createdOn: isNew ? nowIso : formData.createdOn || nowIso,
      createdBy: isNew ? userLabel : formData.createdBy || userLabel,
      modifiedOn: nowIso,
      modifiedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));
    if (!validateFull()) return;
    const activationErrors = collectActivationErrors(formData, allRules, formData.id || undefined);
    if (activationErrors.length) {
      setErrors((prev) => ({ ...prev, activation: activationErrors.join(' ') }));
      return;
    }
    const record = buildRecord(nowIso, userLabel);
    setSaveTestModal({ phase: 'confirm', record });
  };

  const handleSaveWithoutTest = () => {
    if (!saveTestModal?.record) return;
    const record = saveTestModal.record;
    const merged = mergeRulesAfterActivate(allRules, record, publishedBaseline || {});
    setSaveTestModal(null);
    navigate('/admin/configuration/reporting', { state: { rules: merged } });
  };

  const handleRunSaveTest = () => {
    if (!saveTestModal?.record) return;
    const record = saveTestModal.record;
    setSaveTestModal(null);
    navigate(getConfigurationRuleTestPath('reporting', urlNameSegment(record.ruleName)), {
      state: {
        rule: record,
        existingRules: allRules,
        returnPath: `/admin/configuration/reporting/${urlNameSegment(record.ruleName)}/edit`,
      },
    });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    if (isView) {
      const selected = options.find((o) => o.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label} {required && <span className="text-alertred">*</span>}</AdminDetailFieldLabel>
      <div className="relative w-full min-w-0">
        <select
          id={`flg-${name}-select`}
          value={formData[name] || ''}
          title={options.find((o) => o.value === formData[name])?.label || placeholder}
          onChange={(e) => handleChange(name, e.target.value)}
          disabled={disabledExtra}
          className={adminFormDetailSelectClasses(disabledExtra, Boolean(errors[name]))}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <AdminFormDropdownChevron />
      </div>
      {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
    </div>
    );
  };

  const renderCheckboxGroup = (field, label, options, { required = true, disabled = false } = {}) => {
    if (isView) {
      const selectedLabels = options
        .filter((o) => (formData[field] || []).includes(o.value))
        .map((o) => o.label)
        .join(', ');
      return <TextValue label={label} value={selectedLabels || '—'} />;
    }
    return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label} {required && <span className="text-alertred">*</span>}</AdminDetailFieldLabel>
      <div className="space-y-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={`flex items-center gap-2 text-sm ${disabled ? 'text-mediumgray' : 'text-offblack cursor-pointer'}`}
          >
            <input
              type="checkbox"
              className="rounded border-lightgray"
              checked={(formData[field] || []).includes(option.value)}
              onChange={() => toggleArrayValue(field, option.value)}
              disabled={disabled}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {errors[field] && <AdminFormFieldError>{errors[field]}</AdminFormFieldError>}
    </div>
    );
  };

  const showRuleId = Boolean(formData.id);
  const configurationHistory = formData.configurationHistory || [];
  const testHistory = formData.testHistory || [];

  const configurationHistoryTable = configurationHistory.length > 0 ? (
    <div className="space-y-4">
      <AdminPanelTitle className="text-offblack !mb-0">Configuration History</AdminPanelTitle>
      <div className="overflow-x-auto min-w-0">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-lightgray">
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Action</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Field</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Previous</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>New</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Status</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Actor</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Timestamp</AdminDetailTableHeader></th>
            </tr>
          </thead>
          <tbody>
            {[...configurationHistory].reverse().map((row, index) => (
              <tr key={`${row.timestamp}-${index}`} className="border-b border-lightgray last:border-b-0">
                <td className="py-3 px-2"><AdminTableBody>{row.action}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.field}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.previousValue}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.newValue}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.status}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.actor}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{formatAuditDisplay(row.timestamp)}</AdminTableBody></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

  const testHistoryTable = testHistory.length > 0 ? (
    <div className="space-y-4">
      <AdminPanelTitle className="text-offblack !mb-0">Test History</AdminPanelTitle>
      <div className="overflow-x-auto min-w-0">
        <table className="w-full min-w-[560px]">
          <thead>
            <tr className="border-b border-lightgray">
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Actor</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Result</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Summary</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Timestamp</AdminDetailTableHeader></th>
            </tr>
          </thead>
          <tbody>
            {[...testHistory].reverse().map((row, index) => (
              <tr key={`${row.timestamp}-${index}`} className="border-b border-lightgray last:border-b-0">
                <td className="py-3 px-2"><AdminTableBody>{row.actor}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.result}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.summary}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{formatAuditDisplay(row.timestamp)}</AdminTableBody></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

  const testFeedbackPanel = testFeedback ? (
    <DashboardMainContainer className={`${ADMIN_FORM_PANEL_CLASS} mt-4`}>
      <div className="space-y-2">
        <AdminPanelTitle className="text-offblack mb-2 !text-sm">Test Result</AdminPanelTitle>
        <AdminDetailFieldValue className="py-1">{testFeedback}</AdminDetailFieldValue>
        {testDetail ? <pre className="text-xs text-offblack whitespace-pre-wrap font-sans">{testDetail}</pre> : null}
      </div>
    </DashboardMainContainer>
  ) : null;

  const saveModal = (
    <ConfigurationSaveTestModal
      modal={saveTestModal}
      onCancel={() => setSaveTestModal(null)}
      onRunTest={handleRunSaveTest}
      onSaveWithoutTest={handleSaveWithoutTest}
    />
  );

  const ruleNameField = isView ? (
    <TextValue label="Rule Name" value={formData.ruleName} />
  ) : (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>Rule Name <span className="text-alertred">*</span></AdminDetailFieldLabel>
      <input
        id="flg-rule-name"
        type="text"
        placeholder="Enter rule name"
        value={formData.ruleName || ''}
        onChange={(e) => handleChange('ruleName', e.target.value)}
        className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
      />
      {errors.ruleName && <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError>}
    </div>
  );

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Flagging Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Rule Setup</AdminPanelTitle>
              {showRuleId ? (
                <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView />
              ) : null}
              <div className={adminFormTwoColGridClass}>
                {ruleNameField}
                {renderSelect('flagType', 'Flag Type', FLAG_TYPE_OPTIONS, 'Select flag type')}
              </div>
            </div>
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Flagging Access</AdminPanelTitle>
              {renderCheckboxGroup('submittedBy', 'Flag Submitted By', FLAGGING_SUBMITTED_BY_OPTIONS, { disabled: !formData.flagType })}
              {renderSelect(
                'flaggingAvailability',
                'Flagging Availability',
                formData.flagType ? availabilityOptions : [],
                formData.flagType ? 'Select flagging availability' : 'Select flag type first',
                !formData.flagType,
              )}
            </div>
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Flag Details</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                {renderSelect(
                  'flagReasonSet',
                  'Flag Reason Set',
                  formData.flagType ? reasonSetOptions : [],
                  formData.flaggingAvailability ? 'Select flag reason set' : 'Select flagging availability first',
                  !formData.flaggingAvailability,
                )}
                {renderSelect(
                  'flagDetailRequirement',
                  'Flag Detail Requirement',
                  FLAG_DETAIL_REQUIREMENT_OPTIONS,
                  formData.flagReasonSet ? 'Select flag detail requirement' : 'Select flag reason set first',
                  !formData.flagReasonSet,
                )}
              </div>
              {showDetailRequiredReasons ? (
                renderCheckboxGroup('detailRequiredReasons', 'Detail-Required Reasons', detailReasonOptions)
              ) : null}
              {renderSelect(
                'duplicateCheck',
                'Duplicate Check',
                DUPLICATE_CHECK_OPTIONS,
                formData.flagDetailRequirement ? 'Select duplicate check' : 'Select flag detail requirement first',
                !formData.flagDetailRequirement,
              )}
            </div>
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Block Option</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                {renderSelect(
                  'blockOption',
                  'Block Option',
                  formData.duplicateCheck ? blockOptionChoices : [],
                  formData.duplicateCheck ? 'Select block option' : 'Select duplicate check first',
                  !formData.duplicateCheck,
                )}
              </div>
              {showBlockOfferingReasons ? (
                renderCheckboxGroup('blockOfferingReasons', 'Block-Offering Reasons', blockReasonOptions)
              ) : null}
            </div>
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Submission Result</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                {renderSelect(
                  'submissionResult',
                  'Submission Result',
                  SUBMISSION_RESULT_OPTIONS,
                  formData.blockOption ? 'Select submission result' : 'Select block option first',
                  !formData.blockOption,
                )}
              </div>
            </div>
          </DashboardMainContainer>
          {configurationHistoryTable ? (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {configurationHistoryTable}
            </DashboardMainContainer>
          ) : null}
          {testHistoryTable ? (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {testHistoryTable}
            </DashboardMainContainer>
          ) : null}
        </div>
        <ConfigurationRuleTestResultPanel testResult={testResultFeedback} />
        {testFeedbackPanel}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Flagging Rule' : 'Edit Flagging Rule'}
        onSubmit={handleSave}
        afterForm={(
          <>
            <ConfigurationRuleTestResultPanel testResult={testResultFeedback} />
            {saveModal}
            {testFeedbackPanel}
          </>
        )}
      >
        {showRuleId ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        {ruleNameField}
        {renderSelect('flagType', 'Flag Type', FLAG_TYPE_OPTIONS, 'Select flag type')}
        {renderCheckboxGroup('submittedBy', 'Flag Submitted By', FLAGGING_SUBMITTED_BY_OPTIONS, { disabled: !formData.flagType })}
        {renderSelect(
          'flaggingAvailability',
          'Flagging Availability',
          formData.flagType ? availabilityOptions : [],
          formData.flagType ? 'Select flagging availability' : 'Select flag type first',
          !formData.flagType,
        )}
        {renderSelect(
          'flagReasonSet',
          'Flag Reason Set',
          formData.flagType ? reasonSetOptions : [],
          formData.flaggingAvailability ? 'Select flag reason set' : 'Select flagging availability first',
          !formData.flaggingAvailability,
        )}
        {renderSelect(
          'flagDetailRequirement',
          'Flag Detail Requirement',
          FLAG_DETAIL_REQUIREMENT_OPTIONS,
          formData.flagReasonSet ? 'Select flag detail requirement' : 'Select flag reason set first',
          !formData.flagReasonSet,
        )}
        {showDetailRequiredReasons ? (
          renderCheckboxGroup('detailRequiredReasons', 'Detail-Required Reasons', detailReasonOptions)
        ) : null}
        {renderSelect(
          'duplicateCheck',
          'Duplicate Check',
          DUPLICATE_CHECK_OPTIONS,
          formData.flagDetailRequirement ? 'Select duplicate check' : 'Select flag detail requirement first',
          !formData.flagDetailRequirement,
        )}
        {renderSelect(
          'blockOption',
          'Block Option',
          formData.duplicateCheck ? blockOptionChoices : [],
          formData.duplicateCheck ? 'Select block option' : 'Select duplicate check first',
          !formData.duplicateCheck,
        )}
        {showBlockOfferingReasons ? (
          renderCheckboxGroup('blockOfferingReasons', 'Block-Offering Reasons', blockReasonOptions)
        ) : null}
        {renderSelect(
          'submissionResult',
          'Submission Result',
          SUBMISSION_RESULT_OPTIONS,
          formData.blockOption ? 'Select submission result' : 'Select block option first',
          !formData.blockOption,
        )}
        {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
        {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
        {configurationHistoryTable}
        {testHistoryTable}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default FlaggingRuleDetail;
